import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CheckCircle2, AlertTriangle, HelpCircle, FileText, ExternalLink } from 'lucide-react';
import api from '../api';

const STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmé',
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircle2,
    iconColor: 'text-green-600',
    description: 'Valeur vérifiée dans les règlements officiels'
  },
  estimated: {
    label: 'Estimé',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: AlertTriangle,
    iconColor: 'text-yellow-600',
    description: 'Basé sur les valeurs typiques pour ce type de zone'
  },
  assumption: {
    label: 'Hypothèse',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: HelpCircle,
    iconColor: 'text-blue-600',
    description: 'Valeur par défaut ou fournie par l\'utilisateur'
  }
};

/**
 * Component to display a zoning rule value with its status and "View Article" button
 */
export const RuleValueWithStatus = ({ 
  label, 
  value, 
  status = 'estimated', 
  zoneCode,
  fieldName,
  article = null 
}) => {
  const [showArticle, setShowArticle] = useState(false);
  const [articleData, setArticleData] = useState(article);
  const [loading, setLoading] = useState(false);

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.estimated;
  const Icon = config.icon;

  const fetchArticle = async () => {
    if (articleData) {
      setShowArticle(true);
      return;
    }

    if (!zoneCode || !fieldName) {
      setShowArticle(true);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/zoning/article/${zoneCode}/${fieldName}`);
      setArticleData(response.data);
    } catch (error) {
      setArticleData({
        field: fieldName,
        article: null,
        message: 'Article non disponible'
      });
    }
    setLoading(false);
    setShowArticle(true);
  };

  return (
    <>
      <div className="flex items-center justify-between py-2 border-b border-border/50">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{value}</span>
          <Badge 
            variant="outline" 
            className={`${config.color} flex items-center gap-1 text-xs`}
            title={config.description}
          >
            <Icon className={`w-3 h-3 ${config.iconColor}`} />
            {config.label}
          </Badge>
          {(zoneCode || article) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={fetchArticle}
              disabled={loading}
              data-testid={`view-article-${fieldName}`}
            >
              <FileText className="w-3 h-3 mr-1" />
              {loading ? '...' : 'Voir article'}
            </Button>
          )}
        </div>
      </div>

      {/* Article Dialog */}
      <Dialog open={showArticle} onOpenChange={setShowArticle}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Référence réglementaire
            </DialogTitle>
            <DialogDescription>
              {label} - {value}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {articleData?.article ? (
              <>
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-mono">
                      {articleData.article}
                    </Badge>
                    {articleData.source && (
                      <span className="text-xs text-muted-foreground">
                        {articleData.source}
                      </span>
                    )}
                  </div>
                  <p className="text-sm italic">
                    &quot;{articleData.excerpt}&quot;
                  </p>
                </div>

                {articleData.disclaimer && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{articleData.disclaimer}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {articleData?.message || 'Aucune référence réglementaire disponible pour ce champ.'}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {articleData?.note || 'Veuillez consulter l\'arrondissement pour validation.'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Summary component showing all rule statuses
 */
export const RuleStatusSummary = ({ fieldStatus = {} }) => {
  const counts = {
    confirmed: 0,
    estimated: 0,
    assumption: 0
  };

  Object.values(fieldStatus).forEach(status => {
    if (counts[status] !== undefined) {
      counts[status]++;
    }
  });

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  if (total === 0) return null;

  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="text-muted-foreground">Fiabilité des données:</span>
      {counts.confirmed > 0 && (
        <div className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-green-600" />
          <span className="text-green-700">{counts.confirmed} confirmés</span>
        </div>
      )}
      {counts.estimated > 0 && (
        <div className="flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 text-yellow-600" />
          <span className="text-yellow-700">{counts.estimated} estimés</span>
        </div>
      )}
      {counts.assumption > 0 && (
        <div className="flex items-center gap-1">
          <HelpCircle className="w-3 h-3 text-blue-600" />
          <span className="text-blue-700">{counts.assumption} hypothèses</span>
        </div>
      )}
    </div>
  );
};

/**
 * Legend component explaining status icons
 */
export const StatusLegend = () => {
  return (
    <div className="bg-muted/30 p-3 rounded-lg text-xs space-y-2">
      <p className="font-medium mb-2">Légende des statuts</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <div key={key} className="flex items-start gap-2">
              <Icon className={`w-4 h-4 flex-shrink-0 ${config.iconColor}`} />
              <div>
                <span className="font-medium">{config.label}:</span>
                <span className="text-muted-foreground ml-1">{config.description}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RuleValueWithStatus;
